import React, { useEffect, useState, useRef, useCallback } from "react";
import useApiStable from "../../utils/useApiStable";
import { useAuth } from "../../context/AuthContext";
import { isUserAdmin } from "../../utils/userUtils";
import "../../styles/Kiosk.css";

const DAYS = [
  { key: "MONDAY", label: "Montag" },
  { key: "TUESDAY", label: "Dienstag" },
  { key: "WEDNESDAY", label: "Mittwoch" },
  { key: "THURSDAY", label: "Donnerstag" },
  { key: "FRIDAY", label: "Freitag" },
  { key: "SATURDAY", label: "Samstag" },
  { key: "SUNDAY", label: "Sonntag" },
];

const emptyDaySchedules = () =>
  Object.fromEntries(DAYS.map(d => [d.key, { enabled: false, startTime: "08:00", endTime: "22:00" }]));

const schedulesToForm = (schedules) => {
  const form = emptyDaySchedules();
  for (const s of schedules) {
    form[s.dayOfWeek] = { enabled: true, startTime: s.startTime, endTime: s.endTime };
  }
  return form;
};

const formToRequests = (form) =>
  Object.entries(form)
    .filter(([, v]) => v.enabled)
    .map(([dayOfWeek, v]) => ({ dayOfWeek, startTime: v.startTime, endTime: v.endTime }));

const TYPE_LABELS = {
  WELCOME_SCREEN: "Willkommen",
  HOME_MATCHES: "Heimspiele",
  IMAGE: "Bild",
  PDF: "PDF",
  FILE: "Datei hochladen",
  WEBSITE: "Webseite",
};

const CUSTOM_TYPES = ["FILE", "WEBSITE"];

const emptyForm = () => ({ type: "FILE", title: "", contentUrl: "", displaySeconds: 30, enabled: true });

const backendTypeFromFile = (file) => file.type === "application/pdf" ? "PDF" : "IMAGE";

const nameWithoutExtension = (filename) => filename.replace(/\.[^/.]+$/, "");

const KioskConfig = () => {
  const api = useApiStable();
  const { user } = useAuth();
  const userIsAdmin = isUserAdmin(user);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSeconds, setBulkSeconds] = useState(30);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labelInput, setLabelInput] = useState("");
  const [scheduleTokenId, setScheduleTokenId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(emptyDaySchedules());
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const dragIndex = useRef(null);
  const tokenPollRef = useRef(null);

  const loadTokens = useCallback(async () => {
    try {
      const data = await api.get("/kiosk/tokens");
      setTokens(Array.isArray(data) ? data : []);
    } catch {}
  }, [api]);

  const openSchedule = async (token) => {
    try {
      const schedules = await api.get(`/kiosk/tokens/${token.id}/schedules`);
      setScheduleForm(schedulesToForm(Array.isArray(schedules) ? schedules : []));
      setScheduleTokenId(token.id);
    } catch {
      setError("Zeitplan konnte nicht geladen werden.");
    }
  };

  const saveSchedule = async () => {
    setScheduleSaving(true);
    setError(null);
    try {
      await api.put(`/kiosk/tokens/${scheduleTokenId}/schedules`, formToRequests(scheduleForm));
      setScheduleTokenId(null);
    } catch {
      setError("Zeitplan konnte nicht gespeichert werden.");
    } finally {
      setScheduleSaving(false);
    }
  };

  useEffect(() => {
    if (!userIsAdmin) return;
    loadTokens();
    tokenPollRef.current = setInterval(loadTokens, 5000);
    return () => clearInterval(tokenPollRef.current);
  }, [loadTokens, userIsAdmin]);

  const handleActivateToken = async (token) => {
    try {
      await api.put(`/kiosk/tokens/${token.id}/activate`, {});
      await loadTokens();
    } catch {
      setError("Token konnte nicht aktiviert werden.");
    }
  };

  const handleSwitchOn = async (token) => {
    try {
      await api.put(`/kiosk/tokens/${token.id}/switchOn`, {});
      await loadTokens();
    } catch {
      setError("Kiosk konnte nicht angeschaltet werden.");
    }
  };

  const handleSwitchOff = async (token) => {
    try {
      await api.put(`/kiosk/tokens/${token.id}/switchOff`, {});
      await loadTokens();
    } catch {
      setError("Kiosk konnte nicht ausgeschaltet werden.");
    }
  };

  const handleNextSlide = async (token) => {
    try {
      await api.post(`/kiosk/tokens/${token.id}/nextSlide`, {});
    } catch {
      setError("Weiter-Befehl konnte nicht gesendet werden.");
    }
  };

  const handlePrevSlide = async (token) => {
    try {
      await api.post(`/kiosk/tokens/${token.id}/prevSlide`, {});
    } catch {
      setError("Zurück-Befehl konnte nicht gesendet werden.");
    }
  };



  const handleRevokeToken = async (token) => {
    if (!window.confirm(`Gerät "${token.label || token.token.substring(0, 8).toUpperCase()}" wirklich entfernen?`)) return;
    try {
      await api.delete(`/kiosk/tokens/${token.id}`);
      await loadTokens();
    } catch {
      setError("Token konnte nicht entfernt werden.");
    }
  };

  const saveTokenLabel = async (token) => {
    try {
      await api.patch(`/kiosk/tokens/${token.id}`, { label: labelInput });
      setEditingLabelId(null);
      await loadTokens();
    } catch {
      setError("Bezeichnung konnte nicht gespeichert werden.");
    }
  };

  const shortCode = (t) => t.token.replace(/-/g, "").substring(0, 8).toUpperCase();

  const loadSlides = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const data = await api.get("/kiosk/slides");
      setSlides(Array.isArray(data) ? data : []);
      setError(null);
    } catch {
      setError("Folien konnten nicht geladen werden.");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => { loadSlides(true); }, []); // eslint-disable-line

  // --- single-slide actions ---

  const handleToggle = async (slide) => {
    try {
      await api.put(`/kiosk/slides/${slide.id}`, { ...slide, enabled: !slide.enabled });
      await loadSlides();
    } catch {
      setError("Fehler beim Aktualisieren der Folie.");
    }
  };

  const handleDelete = async (slide) => {
    if (!window.confirm(`Folie "${slide.title || TYPE_LABELS[slide.type]}" wirklich löschen?`)) return;
    try {
      await api.delete(`/kiosk/slides/${slide.id}`);
      setSelectedIds(prev => { const s = new Set(prev); s.delete(slide.id); return s; });
      await loadSlides();
    } catch {
      setError("Folie konnte nicht gelöscht werden.");
    }
  };

  const startEdit = (slide) => {
    setEditingId(slide.id);
    setEditForm({ title: slide.title || "", displaySeconds: slide.displaySeconds });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (slide) => {
    try {
      await api.put(`/kiosk/slides/${slide.id}`, {
        ...slide,
        title: editForm.title,
        displaySeconds: Number(editForm.displaySeconds) || 30,
      });
      setEditingId(null);
      await loadSlides();
    } catch {
      setError("Änderungen konnten nicht gespeichert werden.");
    }
  };

  // --- selection ---

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const selectableSlides = slides.filter(s => s.deletable);
  const allSelected = selectableSlides.length > 0 && selectableSlides.every(s => selectedIds.has(s.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableSlides.map(s => s.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // --- bulk actions ---

  const selectedSlides = slides.filter(s => selectedIds.has(s.id));
  const deletableSelected = selectedSlides.filter(s => s.deletable);

  const handleBulkDelete = async () => {
    if (deletableSelected.length === 0) return;
    if (!window.confirm(`${deletableSelected.length} Folie(n) wirklich löschen?`)) return;
    setBulkWorking(true);
    setError(null);
    try {
      for (const slide of deletableSelected) {
        await api.delete(`/kiosk/slides/${slide.id}`);
      }
      setSelectedIds(new Set());
      await loadSlides();
    } catch {
      setError("Beim Löschen ist ein Fehler aufgetreten.");
      await loadSlides();
    } finally {
      setBulkWorking(false);
    }
  };

  const handleBulkSetDuration = async () => {
    const seconds = Number(bulkSeconds);
    if (!seconds || seconds < 5) { setError("Anzeigedauer muss mindestens 5 Sekunden betragen."); return; }
    setBulkWorking(true);
    setError(null);
    try {
      for (const slide of selectedSlides) {
        await api.put(`/kiosk/slides/${slide.id}`, { ...slide, displaySeconds: seconds });
      }
      await loadSlides();
    } catch {
      setError("Beim Aktualisieren der Anzeigezeit ist ein Fehler aufgetreten.");
      await loadSlides();
    } finally {
      setBulkWorking(false);
    }
  };

  // --- add form ---

  useEffect(() => {
    const urls = selectedFiles.map(file => ({
      url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      isPdf: file.type === "application/pdf",
      name: file.name,
    }));
    setPreviewUrls(urls);
    return () => urls.forEach(u => u.url && URL.revokeObjectURL(u.url));
  }, [selectedFiles]);

  const handleRotate = async (slide) => {
    try {
      await api.post(`/kiosk/slides/${slide.id}/rotate`, {});
      await loadSlides();
    } catch {
      setError("Bild konnte nicht gedreht werden.");
    }
  };

  const handleFormChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === "type") setSelectedFiles([]);
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles(files);
    setForm(f => ({ ...f, contentUrl: "" }));
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const response = await api.fetchRaw("/kiosk/upload", { method: "POST", body: fd });
    return response.json();
  };

  const handleAddSlide = async (e) => {
    e.preventDefault();
    if (form.type === "FILE") {
      if (selectedFiles.length === 0) {
        setError("Bitte mindestens eine Datei wählen."); return;
      }
    } else if (!form.contentUrl.trim()) {
      setError("Bitte eine URL angeben."); return;
    }
    setSaving(true);
    setError(null);
    try {
      const displaySeconds = Number(form.displaySeconds) || 30;
      if (form.type === "FILE") {
        for (const file of selectedFiles) {
          const result = await uploadFile(file);
          const title = form.title.trim() || nameWithoutExtension(file.name);
          await api.post("/kiosk/slides", { type: backendTypeFromFile(file), title, contentUrl: result.url, displaySeconds, enabled: form.enabled });
        }
      } else {
        await api.post("/kiosk/slides", { ...form, displaySeconds });
      }
      setShowAddForm(false);
      setForm(emptyForm());
      setSelectedFiles([]);
      await loadSlides();
    } catch {
      setError("Folie(n) konnten nicht erstellt werden.");
    } finally {
      setSaving(false);
    }
  };

  // --- drag & drop (disabled during selection) ---

  const handleDragStart = (e, index) => {
    if (selectedIds.size > 0) { e.preventDefault(); return; }
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (selectedIds.size > 0 || dragIndex.current === null || dragIndex.current === index) return;
    const reordered = [...slides];
    const [moved] = reordered.splice(dragIndex.current, 1);
    reordered.splice(index, 0, moved);
    dragIndex.current = index;
    setSlides(reordered);
  };

  const handleDrop = async () => {
    dragIndex.current = null;
    if (selectedIds.size > 0) return;
    try {
      await api.put("/kiosk/slides/order", { orderedIds: slides.map(s => s.id) });
    } catch {
      setError("Reihenfolge konnte nicht gespeichert werden.");
      await loadSlides();
    }
  };

  const needsFileUpload = form.type === "FILE";
  const multipleFiles = needsFileUpload && selectedFiles.length > 1;

  return (
    <div className="kiosk-config">
      <div className="kiosk-config__header">
        <h2 className="kiosk-config__title">Kiosk-Konfiguration</h2>
        <div className="kiosk-config__actions">
          {userIsAdmin && (
            <a href="/kiosk" target="_blank" rel="noreferrer" className="kiosk-config-btn kiosk-config-btn--kiosk">
              Kiosk öffnen
            </a>
          )}
          <button
            onClick={() => { setShowAddForm(f => !f); setForm(emptyForm()); setSelectedFiles([]); setError(null); }}
            className="kiosk-config-btn kiosk-config-btn--primary"
          >
            + Folie hinzufügen
          </button>
        </div>
      </div>

      {showAddForm && (
        <form className="kiosk-add-form" onSubmit={handleAddSlide}>
          <h3 className="kiosk-add-form__title">Neue Folie{multipleFiles ? `n (${selectedFiles.length})` : ""}</h3>
          <div className="kiosk-add-form__grid">
            <div className="kiosk-form-group">
              <label>Typ</label>
              <div className="kiosk-type-switch">
                {CUSTOM_TYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    className={`kiosk-type-switch__option${form.type === t ? " kiosk-type-switch__option--active" : ""}`}
                    onClick={() => handleFormChange("type", t)}
                  >
                    {TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
            <div className="kiosk-form-group">
              <label>Anzeigedauer (Sekunden)</label>
              <input
                type="number"
                min="5"
                max="300"
                value={form.displaySeconds}
                onChange={e => handleFormChange("displaySeconds", e.target.value)}
              />
            </div>
            <div className="kiosk-form-group kiosk-form-group--full">
              <label>
                Titel{multipleFiles ? " (optional – leer = Dateiname je Folie)" : " (optional)"}
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => handleFormChange("title", e.target.value)}
                placeholder={multipleFiles ? "Gemeinsamer Titel oder leer lassen" : "z.B. Vereinsplan"}
              />
            </div>
            {needsFileUpload && (
              <div className="kiosk-form-group kiosk-form-group--full">
                <label>Datei(en)</label>
                <div className="kiosk-file-drop-zone">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="kiosk-config-btn kiosk-config-btn--neutral"
                  >
                    {selectedFiles.length > 0 ? "Auswahl ändern" : "Datei(en) wählen"}
                  </button>
                  <span className="kiosk-file-drop-zone__hint">Bilder (JPG, PNG, …) oder PDF</span>
                </div>
                {selectedFiles.length > 0 && (
                  <div className="kiosk-preview-grid">
                    {previewUrls.map((p, i) => (
                      <div key={i} className="kiosk-preview-item">
                        {p.url
                          ? <img src={p.url} alt={p.name} className="kiosk-preview-item__thumb" />
                          : <div className="kiosk-preview-item__pdf">PDF</div>
                        }
                        <span className="kiosk-preview-item__name">{p.name}</span>
                        <button
                          type="button"
                          className="kiosk-preview-item__remove"
                          onClick={() => removeSelectedFile(i)}
                          title="Entfernen"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFilesSelected}
                />
              </div>
            )}
            {form.type === "WEBSITE" && (
              <div className="kiosk-form-group kiosk-form-group--full">
                <label>URL</label>
                <input
                  type="url"
                  value={form.contentUrl}
                  onChange={e => handleFormChange("contentUrl", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 24, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
            <button type="submit" className="kiosk-config-btn kiosk-config-btn--primary" disabled={saving}>
              {saving ? "Speichert…" : multipleFiles ? `${selectedFiles.length} Folien erstellen` : "Folie erstellen"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setForm(emptyForm()); setSelectedFiles([]); setError(null); }}
              className="kiosk-config-btn kiosk-config-btn--neutral"
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {error && <div className="kiosk-error">{error}</div>}

      {/* Device token management — Admin-only: Geräte-Freigabe/-Steuerung ist eine
          Sicherheitsentscheidung, die Vorstand/KIOSK-Rolle nicht treffen soll. */}
      {userIsAdmin && tokens.length > 0 && (
        <div className="kiosk-tokens">
          <h3 className="kiosk-tokens__title">Zugelassene Geräte</h3>
          <div className="kiosk-token-list">
            {tokens.map(t => (
              <div key={t.id} className={`kiosk-token-card${t.active ? "" : " kiosk-token-card--pending"}`}>
                <div className="kiosk-token-card__row">
                <div className="kiosk-token-card__code">{shortCode(t)}</div>
                <div className="kiosk-token-card__info">
                  {editingLabelId === t.id ? (
                    <div className="kiosk-token-card__label-edit">
                      <input
                        type="text"
                        value={labelInput}
                        onChange={e => setLabelInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveTokenLabel(t)}
                        placeholder="Gerätebezeichnung"
                        className="kiosk-token-card__label-input"
                        autoFocus
                      />
                      <button onClick={() => saveTokenLabel(t)} className="kiosk-config-btn kiosk-config-btn--success kiosk-config-btn--sm kiosk-config-btn--icon">✓</button>
                      <button onClick={() => setEditingLabelId(null)} className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm kiosk-config-btn--icon">✕</button>
                    </div>
                  ) : (
                    <span
                      className="kiosk-token-card__label"
                      onClick={() => { setEditingLabelId(t.id); setLabelInput(t.label || ""); }}
                      title="Klicken zum Bearbeiten"
                    >
                      {t.label || <em className="kiosk-muted">Kein Name</em>}
                    </span>
                  )}
                  <span className={`kiosk-token-card__status${t.active ? " kiosk-token-card__status--active" : ""}`}>
                    {t.active ? "Aktiv" : "Ausstehend"}
                  </span>
                </div>
                <div className="kiosk-token-card__actions">
                  {!t.active && (
                    <button onClick={() => handleActivateToken(t)} className="kiosk-config-btn kiosk-config-btn--success kiosk-config-btn--sm">
                      Aktivieren
                    </button>
                  )}
                  {t.active && (
                    <>
                      <button onClick={() => handlePrevSlide(t)} className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm" title="Vorherige Folie">
                        ◀ Zurück
                      </button>
                      <button onClick={() => handleNextSlide(t)} className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm" title="Nächste Folie">
                        Weiter ▶
                      </button>
                      <button onClick={() => handleSwitchOn(t)} className="kiosk-config-btn kiosk-config-btn--success kiosk-config-btn--sm">
                        Anschalten
                      </button>
                      <button onClick={() => handleSwitchOff(t)} className="kiosk-config-btn kiosk-config-btn--danger kiosk-config-btn--sm">
                        Ausschalten
                      </button>
                      <button
                        onClick={() => scheduleTokenId === t.id ? setScheduleTokenId(null) : openSchedule(t)}
                        className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm"
                      >
                        Zeitplan
                      </button>
                    </>
                  )}
                  <button onClick={() => handleRevokeToken(t)} className="kiosk-config-btn kiosk-config-btn--danger kiosk-config-btn--sm">
                    Entfernen
                  </button>
                </div>
                </div>
                {scheduleTokenId === t.id && (
                  <div className="kiosk-schedule-editor">
                    <div className="kiosk-schedule-table-wrap">
                      <table className="kiosk-schedule-table">
                        <thead>
                          <tr>
                            <th>Tag</th>
                            <th>Aktiv</th>
                            <th>Von</th>
                            <th>Bis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {DAYS.map(d => (
                            <tr key={d.key}>
                              <td>{d.label}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={scheduleForm[d.key].enabled}
                                  onChange={e => setScheduleForm(f => ({ ...f, [d.key]: { ...f[d.key], enabled: e.target.checked } }))}
                                />
                              </td>
                              <td>
                                <input
                                  type="time"
                                  value={scheduleForm[d.key].startTime}
                                  disabled={!scheduleForm[d.key].enabled}
                                  onChange={e => setScheduleForm(f => ({ ...f, [d.key]: { ...f[d.key], startTime: e.target.value } }))}
                                  style={{ width: 90 }}
                                />
                              </td>
                              <td>
                                <input
                                  type="time"
                                  value={scheduleForm[d.key].endTime}
                                  disabled={!scheduleForm[d.key].enabled}
                                  onChange={e => setScheduleForm(f => ({ ...f, [d.key]: { ...f[d.key], endTime: e.target.value } }))}
                                  style={{ width: 90 }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <button onClick={saveSchedule} disabled={scheduleSaving} className="kiosk-config-btn kiosk-config-btn--success kiosk-config-btn--sm">
                        {scheduleSaving ? "Speichert..." : "Zeitplan speichern"}
                      </button>
                      <button onClick={() => setScheduleTokenId(null)} className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="kiosk-bulk-bar">
          <span className="kiosk-bulk-bar__count">{selectedIds.size} ausgewählt</span>
          <div className="kiosk-bulk-bar__duration">
            <input
              type="number"
              min="5"
              max="300"
              value={bulkSeconds}
              onChange={e => setBulkSeconds(e.target.value)}
              className="kiosk-bulk-bar__seconds"
              title="Anzeigedauer in Sekunden"
            />
            <span className="kiosk-bulk-bar__unit">s</span>
            <button
              onClick={handleBulkSetDuration}
              disabled={bulkWorking}
              className="kiosk-config-btn kiosk-config-btn--primary"
            >
              Anzeigezeit setzen
            </button>
          </div>
          {deletableSelected.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkWorking}
              className="kiosk-config-btn kiosk-config-btn--danger"
            >
              {deletableSelected.length} löschen
            </button>
          )}
          <button onClick={clearSelection} className="kiosk-config-btn kiosk-config-btn--neutral">
            Auswahl aufheben
          </button>
        </div>
      )}

      {loading ? (
        <p>Lade Folien...</p>
      ) : (
        <div className="kiosk-slide-list">
          {/* Select-all header row */}
          {slides.length > 1 && (
            <div className="kiosk-select-all">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.85rem", color: "#555" }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                {allSelected ? "Alle abwählen" : "Alle auswählen"}
              </label>
            </div>
          )}

          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`kiosk-slide-card${slide.enabled ? "" : " kiosk-slide-card--disabled"}${selectedIds.has(slide.id) ? " kiosk-slide-card--selected" : ""}`}
              draggable={editingId !== slide.id && selectedIds.size === 0}
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={handleDrop}
            >
              {slide.deletable ? (
                <input
                  type="checkbox"
                  className="kiosk-slide-card__checkbox"
                  checked={selectedIds.has(slide.id)}
                  onChange={() => toggleSelect(slide.id)}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span className="kiosk-slide-card__checkbox-placeholder" />
              )}

              {selectedIds.size === 0 && (
                <span className="kiosk-slide-card__drag" title="Ziehen zum Sortieren">⠿</span>
              )}

              {editingId === slide.id ? (
                <div className="kiosk-slide-card__edit">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Titel (optional)"
                      style={{ flex: 1, minWidth: 120, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: "0.9rem" }}
                    />
                    <input
                      type="number"
                      value={editForm.displaySeconds}
                      onChange={e => setEditForm(f => ({ ...f, displaySeconds: e.target.value }))}
                      min="5" max="300"
                      title="Anzeigedauer in Sekunden"
                      style={{ width: 70, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4, fontSize: "0.9rem" }}
                    />
                    <span style={{ fontSize: "0.8rem", color: "#888" }}>s</span>
                    <button onClick={() => saveEdit(slide)} className="kiosk-config-btn kiosk-config-btn--success kiosk-config-btn--sm">
                      Speichern
                    </button>
                    <button onClick={cancelEdit} className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm">
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {slide.type === "IMAGE" && slide.contentUrl && (
                    <div className="kiosk-slide-thumb">
                      <img src={slide.contentUrl} alt="" className="kiosk-slide-thumb__img" />
                    </div>
                  )}
                  <div className="kiosk-slide-card__info">
                    <div className="kiosk-slide-card__type">{TYPE_LABELS[slide.type] ?? slide.type}</div>
                    <div className="kiosk-slide-card__title">{slide.title || <em className="kiosk-muted">kein Titel</em>}</div>
                    <div className="kiosk-slide-card__meta">{slide.displaySeconds}s</div>
                  </div>
                  <div className="kiosk-slide-card__controls">
                    {slide.type === "IMAGE" && (
                      <button
                        onClick={() => handleRotate(slide)}
                        className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm kiosk-config-btn--icon"
                        title="90° im Uhrzeigersinn drehen"
                      >
                        ↻
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(slide)}
                      className="kiosk-config-btn kiosk-config-btn--neutral kiosk-config-btn--sm kiosk-config-btn--icon"
                      title="Bearbeiten"
                    >
                      ✎
                    </button>
                    <label className="kiosk-toggle" title={slide.enabled ? "Deaktivieren" : "Aktivieren"}>
                      <input type="checkbox" checked={slide.enabled} onChange={() => handleToggle(slide)} />
                      <span className="kiosk-toggle__slider" />
                    </label>
                    {slide.deletable && (
                      <button
                        onClick={() => handleDelete(slide)}
                        className="kiosk-config-btn kiosk-config-btn--danger kiosk-config-btn--sm"
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
          {slides.length === 0 && <p style={{ color: "#888", fontStyle: "italic" }}>Keine Folien vorhanden.</p>}
        </div>
      )}

    </div>
  );
};

export default KioskConfig;
